import { AppSelect, AppAutocomplete, AppTextField } from "components/common/inputs";
import { AppDialog } from "components/common/dialogs";
import { AppButton } from "components/common/buttons";

import React, { useState, useEffect } from "react";
import {
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import { DateTimePicker, DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { vi } from "date-fns/locale";
import {
  Save,
  Search,
} from "@mui/icons-material";
import { getToken } from "services/auth.service";
import ContractSelectionModal from "../calendars/ContractSelectionModal";

const AddEventDialog = ({
  open,
  onClose,
  onEventCreated,
  selectedDate,
  editingEvent,
}) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    startDate: new Date(),
    endDate: new Date(),
    isAllDay: false,
    isRecurring: false,
    recurrencePattern: "",
    recurrenceEndDate: null,
    contractId: null,
    notes: "",
    color: "#2196f3",
    // Notification settings
    notificationSettings: {
      notify1Day: false, // 1440 phút
      notify2Hours: false, // 120 phút
      notify30Min: false, // 30 phút
    },
  });

  const [users, setUsers] = useState([]);
  const [contractUsers, setContractUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedContractUsers, setSelectedContractUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState("");
  const [editEventSharedUsers, setEditEventSharedUsers] = useState([]); // Store shared users từ event đang edit

  // Force reset form data when dialog first opens for create mode
  useEffect(() => {
    if (open) {
      // Force reset nếu không có editingEvent (create mode)
      if (!editingEvent) {
        setTimeout(() => {
          const startDate = selectedDate ? new Date(selectedDate) : new Date();
          const endDate = selectedDate ? new Date(selectedDate) : new Date();

          setFormData({
            title: "",
            description: "",
            startDate: startDate,
            endDate: endDate,
            isAllDay: false,
            isRecurring: false,
            recurrencePattern: "",
            recurrenceEndDate: null,
            contractId: null,
            notes: "",
            color: "#2196f3",
            // Reset notification settings
            notificationSettings: {
              notify1Day: false,
              notify2Hours: false,
              notify30Min: false,
            },
          });
          setSelectedUsers([]);
          setSelectedContractUsers([]);
          setContractUsers([]);
          setSelectedContract(null);
          setEditEventSharedUsers([]);
          setError("");

          // Load users for create mode
          fetchUsers();
        }, 0);
      }
    }
  }, [open, editingEvent, selectedDate]);

  // Handle editing existing event
  useEffect(() => {
    const loadEventDataForEdit = async () => {
      if (editingEvent && open) {
        // Load users trước khi xử lý edit data
        await fetchUsers();

        const event = editingEvent.extendedProps || editingEvent;
        setFormData({
          title: editingEvent.title || "",
          description: event.description || "",
          startDate: new Date(editingEvent.start || editingEvent.startDate),
          endDate: new Date(editingEvent.end || editingEvent.endDate),
          isAllDay: editingEvent.allDay || event.isAllDay || false,
          isRecurring: event.isRecurring || false,
          recurrencePattern: event.recurrencePattern || "",
          recurrenceEndDate: event.recurrenceEndDate
            ? new Date(event.recurrenceEndDate)
            : null,
          contractId: event.contractId || "",
          notes: event.notes || "",
          eventType: event.eventType || "manual",
          color: editingEvent.backgroundColor || event.color || "#2196f3",
          // Default notification settings - sẽ được load từ API
          notificationSettings: {
            notify1Day: false,
            notify2Hours: false,
            notify30Min: false,
          },
        });

        // Load existing notification settings
        try {
          const token = getToken();
          const settingsResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/api/Events/${editingEvent.id || editingEvent.eventId
            }/notification-settings`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (settingsResponse.ok) {
            const settings = await settingsResponse.json();
            const notificationSettings = {
              notify1Day: settings.some((s) => s.notifyBeforeMinutes === 1440),
              notify2Hours: settings.some((s) => s.notifyBeforeMinutes === 120),
              notify30Min: settings.some((s) => s.notifyBeforeMinutes === 30),
            };

            setFormData((prev) => ({
              ...prev,
              notificationSettings,
            }));
          }
        } catch (error) {
          setError("Không thể tải đủ dữ liệu sự kiện. Hãy tải lại trước khi lưu.");
          console.error("Error loading notification settings:", error);
        }

        // Load contract info and users if contractId exists
        if (event.contractId) {
          try {
            const token = getToken();

            // Load contract details
            if (event.contractNumber && event.contractName) {
              setSelectedContract({
                contractId: event.contractId,
                contractNumber: event.contractNumber,
                contractName: event.contractName,
              });
            } else {
              // Hiển thị thông tin cơ bản mà không có "Loading..."
              setSelectedContract({
                contractId: event.contractId,
                contractNumber:
                  event.contractNumber || `Contract ${event.contractId}`,
                contractName: event.contractName || "Dự án không xác định",
              });
            }

            // Load contract users
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/Events/contract-users/${event.contractId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );

            if (response.ok) {
              const data = await response.json();

              if (Array.isArray(data)) {
                setContractUsers(data);
                // Không auto select khi edit - sẽ được handle bởi useEffect khác
              } else {
                console.error("Contract users data is not an array:", data);
                setContractUsers([]);
              }
            } else {
              console.error("Failed to fetch contract users:", response.status);
              setContractUsers([]);
            }
          } catch (error) {
            setError("Không thể tải đủ dữ liệu sự kiện. Hãy tải lại trước khi lưu.");
            console.error("Error fetching contract users:", error);
            setContractUsers([]);
          }
        }

        // Load shared users when editing
        try {
          const token = getToken();
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/Events/${editingEvent.id || editingEvent.eventId
            }`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (response.ok) {
            const eventDetail = await response.json();

            if (
              eventDetail.sharedUsers &&
              Array.isArray(eventDetail.sharedUsers)
            ) {
              // Store shared users để phân loại sau khi contract users được load
              setEditEventSharedUsers(
                eventDetail.sharedUsers.map((u) => u.userId)
              );
            }
          }
        } catch (error) {
          setError("Không thể tải đủ dữ liệu sự kiện. Hãy tải lại trước khi lưu.");
          console.error("Error fetching event detail:", error);
        }
      }
    };

    loadEventDataForEdit();
  }, [editingEvent, open]);

  // Phân loại shared users khi contractUsers hoặc editEventSharedUsers thay đổi
  useEffect(() => {
    if (editEventSharedUsers.length > 0) {
      const contractUserIds = contractUsers.map((u) => u.userId);
      const generalUsers = [];
      const contractUsersFromShared = [];

      editEventSharedUsers.forEach((userId) => {
        if (contractUserIds.includes(userId)) {
          contractUsersFromShared.push(userId);
        } else {
          generalUsers.push(userId);
        }
      });

      setSelectedUsers(generalUsers);
      setSelectedContractUsers(contractUsersFromShared);
    }
  }, [contractUsers, editEventSharedUsers]);

  // Fetch users để share
  const fetchUsers = async () => {
    try {
      setUsersLoading(true);
      const token = getToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Events/users-for-sharing`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (Array.isArray(data)) {
          setUsers(data);
        } else {
          console.error("Users data is not an array:", data);
          setUsers([]);
        }
      } else {
        console.error("Failed to fetch users:", response.status);
        setUsers([]);
      }
    } catch (error) {
      setError("Không thể tải đủ dữ liệu sự kiện. Hãy tải lại trước khi lưu.");
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle contract selection change
  const handleContractChange = async (contractId) => {
    setFormData({ ...formData, contractId });

    if (contractId === null) {
      // Clear contract selection
      setSelectedContract(null);
      setContractUsers([]);
      setSelectedContractUsers([]);
      return;
    }

    if (contractId) {
      try {
        const token = getToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/Events/contract-users/${contractId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();

          if (Array.isArray(data)) {
            setContractUsers(data);

            if (!editingEvent) {
              // Tạo mới: auto select tất cả users trong contract
              setSelectedContractUsers(data.map((u) => u.userId));
            } else {
              // Edit mode: phân loại lại shared users dựa trên hợp đồng mới
              if (editEventSharedUsers.length > 0) {
                const newContractUserIds = data.map((u) => u.userId);
                const generalUsers = [];
                const contractUsersFromShared = [];

                editEventSharedUsers.forEach((userId) => {
                  if (newContractUserIds.includes(userId)) {
                    contractUsersFromShared.push(userId);
                  } else {
                    generalUsers.push(userId);
                  }
                });

                setSelectedUsers(generalUsers);
                setSelectedContractUsers(contractUsersFromShared);
              }
            }
          } else {
            console.error("Contract users data is not an array:", data);
            setContractUsers([]);
            setSelectedContractUsers([]);
          }
        } else {
          console.error("Failed to fetch contract users:", response.status);
          setContractUsers([]);
          setSelectedContractUsers([]);
        }
      } catch (error) {
        setError("Không thể tải đủ dữ liệu sự kiện. Hãy tải lại trước khi lưu.");
        console.error("Error fetching contract users:", error);
        setContractUsers([]);
        setSelectedContractUsers([]);
      }
    } else {
      // Không chọn hợp đồng: chuyển tất cả contract users thành general users
      if (editingEvent && selectedContractUsers.length > 0) {
        const allCurrentUsers = [...selectedUsers, ...selectedContractUsers];
        setSelectedUsers([...new Set(allCurrentUsers)]);
      }
      setContractUsers([]);
      setSelectedContractUsers([]);
    }
  };

  // Handle contract selection from modal
  const handleContractSelect = (contract) => {
    setSelectedContract(contract);
    handleContractChange(contract.contractId);
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      if (!formData.title.trim()) {
        setError("Tiêu đề sự kiện không được để trống");
        return;
      }

      if (formData.startDate > formData.endDate) {
        setError("Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc");
        return;
      }

      if (formData.isRecurring && !formData.recurrencePattern) {
        setError("Vui lòng chọn kiểu lặp lại");
        return;
      }

      if (formData.isRecurring && !formData.recurrenceEndDate) {
        setError("Vui lòng chọn ngày kết thúc lặp lại");
        return;
      }

      // Combine selected users
      const allSelectedUsers = [...selectedUsers, ...selectedContractUsers];
      const uniqueUsers = [...new Set(allSelectedUsers)];

      // Prepare notification settings
      const notificationMinutes = [];
      if (formData.notificationSettings.notify1Day)
        notificationMinutes.push(1440);
      if (formData.notificationSettings.notify2Hours)
        notificationMinutes.push(120);
      if (formData.notificationSettings.notify30Min)
        notificationMinutes.push(30);

      // Format dates to avoid timezone issues
      const formatDateForBackend = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const isEditing = !!editingEvent;

      // Prepare data based on create vs update
      let eventData;
      if (isEditing) {
        // UpdateEventRequest structure
        eventData = {
          title: formData.title,
          description: formData.description,
          startDate: formatDateForBackend(formData.startDate),
          endDate: formatDateForBackend(formData.endDate),
          isAllDay: formData.isAllDay,
          isRecurring: formData.isRecurring,
          recurrencePattern: formData.recurrencePattern,
          recurrenceEndDate: formData.recurrenceEndDate
            ? formatDateForBackend(formData.recurrenceEndDate)
            : null,
          contractId:
            formData.contractId && formData.contractId !== ""
              ? parseInt(formData.contractId)
              : null,
          notes: formData.notes,
          color: formData.color,
          sharedUserIds: uniqueUsers,
          notificationMinutes: notificationMinutes,
        };
      } else {
        // CreateEventRequest structure
        eventData = {
          title: formData.title,
          description: formData.description,
          startDate: formatDateForBackend(formData.startDate),
          endDate: formatDateForBackend(formData.endDate),
          isAllDay: formData.isAllDay,
          isRecurring: formData.isRecurring,
          recurrencePattern: formData.recurrencePattern,
          recurrenceEndDate: formData.recurrenceEndDate
            ? formatDateForBackend(formData.recurrenceEndDate)
            : null,
          contractId:
            formData.contractId && formData.contractId !== ""
              ? parseInt(formData.contractId)
              : null,
          notes: formData.notes,
          eventType: "manual",
          color: formData.color,
          sharedUserIds: uniqueUsers,
          notificationMinutes: notificationMinutes,
        };
      }

      const token = getToken();
      const eventId = isEditing
        ? editingEvent.id || editingEvent.eventId
        : null;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/Events${isEditing ? `/${eventId}` : ""
        }`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(eventData),
        }
      );

      if (response.ok) {
        onEventCreated();
      } else {
        const errorData = await response.json();
        setError(
          errorData.message ||
          (isEditing
            ? "Có lỗi xảy ra khi cập nhật sự kiện"
            : "Có lỗi xảy ra khi tạo sự kiện")
        );
      }
    } catch (error) {
      console.error("Error creating event:", error);
      setError(
        editingEvent
          ? "Có lỗi xảy ra khi cập nhật sự kiện"
          : "Có lỗi xảy ra khi tạo sự kiện"
      );
    } finally {
      setLoading(false);
    }
  };

  const colorOptions = [
    { value: "#2196f3", label: "Xanh dương", color: "#2196f3" },
    { value: "#4caf50", label: "Xanh lá", color: "#4caf50" },
    { value: "#ff9800", label: "Cam", color: "#ff9800" },
    { value: "#f44336", label: "Đỏ", color: "#f44336" },
    { value: "#9c27b0", label: "Tím", color: "#9c27b0" },
    { value: "#607d8b", label: "Xám xanh", color: "#607d8b" },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
      <AppDialog
        open={open}
        onClose={onClose}
        title={editingEvent ? "Chỉnh sửa sự kiện" : "Tạo sự kiện mới"}
        size="md"
        loading={loading}
        PaperProps={{ sx: { minHeight: "80vh" } }}
        actions={
          <>
            <AppButton disabled={loading}
              onClick={onClose}
              variant="outlined"
              color="inherit"
              sx={{ textTransform: "none", mr: 1 }}
            >
              Hủy
            </AppButton>
            <AppButton
              onClick={handleSubmit}
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              disabled={loading}
              sx={{
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading
                ? editingEvent
                  ? "Đang cập nhật..."
                  : "Đang tạo..."
                : editingEvent
                  ? "Cập nhật sự kiện"
                  : "Tạo sự kiện"}
            </AppButton>
          </>
        }
      >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Tiêu đề */}
            <Grid item size={12}>
              <AppTextField
                disabled={loading}
                fullWidth
                label="Tiêu đề sự kiện"
                value={formData.title}
                onChange={(value) => setFormData({ ...formData, title: value })
                }
                required
                variant="outlined"
              />
            </Grid>

            {/* Mô tả */}
            <Grid item size={12}>
              <AppTextField
                disabled={loading}
                fullWidth
                label="Mô tả"
                value={formData.description}
                onChange={(value) => setFormData({ ...formData, description: value })
                }
                multiline
                minRows={3}
                variant="outlined"
              />
            </Grid>

            {/* Thời gian */}
            <Grid item size={6}>
              <DateTimePicker
                disabled={loading}
                label="Từ ngày"
                value={formData.startDate}
                onChange={(date) =>
                  setFormData({ ...formData, startDate: date })
                }
                format="dd/MM/yyyy HH:mm"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                  },
                }}
              />
            </Grid>

            <Grid item size={6}>
              <DateTimePicker
                disabled={loading}
                label="Đến ngày"
                value={formData.endDate}
                onChange={(date) => setFormData({ ...formData, endDate: date })}
                format="dd/MM/yyyy HH:mm"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: "outlined",
                  },
                }}
              />
            </Grid>

            {/* Cả ngày */}
            <Grid item size={12}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={loading}
                    checked={formData.isAllDay}
                    onChange={(e) =>
                      setFormData({ ...formData, isAllDay: e.target.checked })
                    }
                  />
                }
                label="Sự kiện cả ngày"
              />
            </Grid>

            {/* Lặp lại */}
            <Grid item size={6}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={loading}
                    checked={formData.isRecurring}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isRecurring: e.target.checked,
                      })
                    }
                  />
                }
                label="Lặp lại sự kiện"
              />
            </Grid>

            {formData.isRecurring && (
              <>
                <Grid item size={6}>
                  <AppSelect
                    disabled={loading}
                    label="Kiểu lặp lại"
                    value={formData.recurrencePattern}
                    onChange={(value) => setFormData({ ...formData, recurrencePattern: value })}
                    options={[{ value: "daily", label: "Hàng ngày" }, { value: "weekly", label: "Hàng tuần" }]}
                  />
                </Grid>

                <Grid item size={12}>
                  <DatePicker
                    disabled={loading}
                    label="Kết thúc lặp lại vào"
                    value={formData.recurrenceEndDate}
                    onChange={(date) =>
                      setFormData({ ...formData, recurrenceEndDate: date })
                    }
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "outlined",
                      },
                    }}
                  />
                </Grid>
              </>
            )}

            {/* Hợp đồng tham chiếu */}
            <Grid item size={12}>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ mb: 1, fontFamily: "Inter", color: "text.secondary" }}
                >
                  Hợp đồng tham chiếu (tùy chọn)
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AppButton
                    disabled={loading}
                    variant="outlined"
                    onClick={() => setIsContractModalOpen(true)}
                    sx={{
                      fontFamily: "Inter",
                      textTransform: "none",
                      textDecoration: "none",
                      justifyContent: "flex-start",
                      flex: 1,
                      border: "1px solid #e0e0e0",
                    }}
                    startIcon={<Search />}
                  >
                    {selectedContract
                      ? `${selectedContract.contractNumber} - ${selectedContract.contractName}`
                      : "Chọn hợp đồng..."}
                  </AppButton>
                  {selectedContract && (
                    <AppButton
                      disabled={loading}
                      variant="outlined"
                      size="small"
                      onClick={() => handleContractChange(null)}
                      sx={{
                        fontFamily: "Inter",
                        textTransform: "none",
                        textDecoration: "none",
                        minWidth: "auto",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      Xóa
                    </AppButton>
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Chia sẻ với users khác */}
            <Grid item size={12}>
              <AppAutocomplete
                multiple
                label="Chia sẻ với người dùng khác"
                placeholder="Chọn người dùng..."
                options={users.map((user) => ({ ...user, value: user.userId, label: user.fullName }))}
                value={selectedUsers}
                onChange={(ids) => setSelectedUsers(ids)}
                disabled={loading || usersLoading}
                helperText={usersLoading ? "Đang tải người dùng..." : undefined}
              />
            </Grid>

            {/* Users trong cùng hợp đồng */}
            {contractUsers.length > 0 && (
              <Grid item size={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Chia sẻ với người dùng cùng hợp đồng:
                </Typography>
                <AppAutocomplete
                  disabled={loading}
                  multiple
                  label="Chia sẻ với người dùng cùng hợp đồng"
                  placeholder="Chọn người dùng..."
                  options={contractUsers.map((user) => ({ ...user, value: user.userId, label: user.fullName }))}
                  value={selectedContractUsers}
                  onChange={(ids) => setSelectedContractUsers(ids)}
                />
              </Grid>
            )}

            {/* Màu sắc */}
            <Grid item size={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Màu sắc sự kiện:
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {colorOptions.map((option) => (
                  <Box
                    key={option.value}
                    onClick={() =>
                      setFormData({ ...formData, color: option.value })
                    }
                    sx={{
                      width: 40,
                      height: 40,
                      backgroundColor: option.color,
                      borderRadius: 1,
                      cursor: "pointer",
                      border:
                        formData.color === option.value
                          ? "3px solid #1976d2"
                          : "2px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        transform: "scale(1.1)",
                        transition: "transform 0.2s",
                      },
                    }}
                  >
                    {formData.color === option.value && (
                      <Box sx={{ color: "white", fontSize: "1.2rem" }}>✓</Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Thông báo nhắc nhở */}
            <Grid item size={12}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, fontFamily: "Inter" }}
              >
                Thông báo nhắc nhở:
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={loading}
                      checked={formData.notificationSettings.notify1Day}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          notificationSettings: {
                            ...formData.notificationSettings,
                            notify1Day: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Thông báo 1 ngày trước"
                />
                <FormControlLabel
                  control={
                    <Switch
                      disabled={loading}
                      checked={formData.notificationSettings.notify2Hours}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          notificationSettings: {
                            ...formData.notificationSettings,
                            notify2Hours: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Thông báo 2 giờ trước"
                />
                <FormControlLabel
                  control={
                    <Switch
                      disabled={loading}
                      checked={formData.notificationSettings.notify30Min}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          notificationSettings: {
                            ...formData.notificationSettings,
                            notify30Min: e.target.checked,
                          },
                        })
                      }
                    />
                  }
                  label="Thông báo 30 phút trước"
                />
              </Box>
            </Grid>

            {/* Ghi chú */}
            <Grid item size={12}>
              <AppTextField
                disabled={loading}
                fullWidth
                label="Ghi chú"
                value={formData.notes}
                onChange={(value) => setFormData({ ...formData, notes: value })
                }
                multiline
                minRows={3}
                variant="outlined"
              />
            </Grid>
          </Grid>
      </AppDialog>

      {/* Contract Selection Modal */}
      <ContractSelectionModal
        open={isContractModalOpen}
        onClose={() => setIsContractModalOpen(false)}
        onSelect={handleContractSelect}
        currentContractId={formData.contractId}
      />
    </LocalizationProvider>
  );
};

export default AddEventDialog;
